import { QueueProjectsExportColumn } from "components/sidebar/queueProjects/queueProjectsExportColumn";
import { Queue_Projects_Insert_Input } from "generated/graphql";
import { isEqual } from "lodash";
import { GeneratorType } from "types/generatorType";
import {
    maybeConvertToQueueProject,
    QueueProject
} from "types/queueProjectsType";
import { RegionType } from "types/regionType";

export type QueueProjectsByFuelType = Map<
    GeneratorType,
    ReadonlyArray<QueueProject>
>;

export interface QueueProjectStats {
    readonly includedCount: number;
    readonly totalCount: number;
    readonly includedCapacityGw: number;
    readonly totalCapacityGw: number;
}

export const computeQueueProjectStats = (
    projects: ReadonlyArray<QueueProject>
): QueueProjectStats => {
    const includedProjects = projects.filter((p) => p.included);
    return {
        includedCount: includedProjects.length,
        totalCount: projects.length,
        includedCapacityGw:
            includedProjects.reduce((sum, p) => sum + p.erisCapacityMw, 0) /
            1000,
        totalCapacityGw:
            projects.reduce((sum, p) => sum + p.erisCapacityMw, 0) / 1000
    };
};

const sortByProjectNumber = (
    projects: ReadonlyArray<QueueProject>
): ReadonlyArray<QueueProject> =>
    [...projects].sort((a, b) =>
        a.projectNumber.localeCompare(b.projectNumber)
    );

/**
 * Compares two lists of queue projects for equality, sorting by project number.
 * When ignoreIncluded is true, ignores the included field — useful for
 * comparing edited projects against base projects.
 */
export const queueProjectsEqual = (
    a: ReadonlyArray<QueueProject>,
    b: ReadonlyArray<QueueProject>,
    ignoreIncluded?: boolean
): boolean => {
    const sortedA = sortByProjectNumber(a);
    const sortedB = sortByProjectNumber(b);

    if (!ignoreIncluded) {
        return isEqual(sortedA, sortedB);
    }

    const strip = (projects: ReadonlyArray<QueueProject>) =>
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        projects.map(({ included, ...rest }) => rest);

    return isEqual(strip(sortedA), strip(sortedB));
};

export const queueProjectTogglesEqual = (
    a: ReadonlyArray<QueueProject>,
    b: ReadonlyArray<QueueProject>
): boolean => {
    const toggles = (projects: ReadonlyArray<QueueProject>) =>
        sortByProjectNumber(projects).map(({ projectNumber, included }) => ({
            projectNumber,
            included
        }));
    return isEqual(toggles(a), toggles(b));
};

/**
 * Validates queue projects across fuel types. Checks that:
 * 1. Every fuel type has at least one project
 * 2. All fuel types share the same set of projects with the same included values
 */
export type ValidationResult =
    | { valid: true }
    | { valid: false; message: string };

export const validateQueueProjectsByFuelType = (
    map: QueueProjectsByFuelType
): ValidationResult => {
    for (const [, projects] of Array.from(map)) {
        if (projects.length === 0) {
            return {
                valid: false,
                message:
                    "The selected base scope has no queue generators. Please select a different base scope."
            };
        }
    }

    let firstProjects: ReadonlyArray<QueueProject> | undefined;
    for (const [, projects] of Array.from(map)) {
        if (firstProjects === undefined) {
            firstProjects = projects;
        } else if (!queueProjectsEqual(projects, firstProjects)) {
            return {
                valid: false,
                message:
                    "The selected fuel types have different queue generator assumptions. To select multiple fuel types, they must share the same set of base queue assumptions."
            };
        }
    }

    return { valid: true };
};

export type UploadValidationResult =
    | { type: "success"; projects: ReadonlyArray<QueueProject> }
    | { type: "error"; message: string };

const maybeParseRowToQueueProject = (
    row: Record<string, string>,
    exportColumns: ReadonlyArray<QueueProjectsExportColumn>
): QueueProject | undefined => {
    let parsed: Partial<QueueProject> = {};
    for (const column of exportColumns) {
        const value = row[column.getHeader()];
        if (value !== undefined) {
            parsed = { ...parsed, ...column.parseExportValue(value) };
        }
    }
    return maybeConvertToQueueProject(parsed);
};

/**
 * Round-trips a QueueProject through the export columns, producing only the
 * fields that the CSV can represent. Used to compare uploaded projects against
 * base projects without mismatching on fields the CSV doesn't carry.
 */
const roundTripThroughColumns = (
    project: QueueProject,
    exportColumns: ReadonlyArray<QueueProjectsExportColumn>
): QueueProject | undefined => {
    const row: Record<string, string> = {};
    for (const column of exportColumns) {
        row[column.getHeader()] = column.renderExportValue(project);
    }
    return maybeParseRowToQueueProject(row, exportColumns);
};

export const validateAndCreateEditedProjects = (
    parsedRows: ReadonlyArray<Record<string, string>>,
    baseProjects: ReadonlyArray<QueueProject>,
    exportColumns: ReadonlyArray<QueueProjectsExportColumn>
): UploadValidationResult => {
    const uploadedProjects = parsedRows.map((row) =>
        maybeParseRowToQueueProject(row, exportColumns)
    );
    const invalidIndex = uploadedProjects.indexOf(undefined);
    if (invalidIndex !== -1) {
        return {
            type: "error",
            message: `Row ${invalidIndex + 1} is missing required fields.`
        };
    }
    const projects = uploadedProjects as ReadonlyArray<QueueProject>;

    // Should never be undefined — we're round-tripping valid QueueProjects
    // through export columns that produced them.
    const roundTrippedBase = baseProjects
        .map((p) => roundTripThroughColumns(p, exportColumns))
        .filter((p): p is QueueProject => p !== undefined);

    if (!queueProjectsEqual(projects, roundTrippedBase, true)) {
        return {
            type: "error",
            message:
                'Projects were added, removed, or modified. Only the "Included" column can be edited.'
        };
    }

    const includedByProjectNumber = new Map(
        projects.map((p) => [p.projectNumber, p.included])
    );
    const gluedProjects = baseProjects.map((base) => {
        const included = includedByProjectNumber.get(base.projectNumber);
        // Should never happen — validation above confirmed uploaded projects
        // match base projects by project number.
        if (included === undefined) {
            throw new Error(
                `Uploaded projects missing project number ${base.projectNumber}`
            );
        }
        return { ...base, included };
    });

    return { type: "success", projects: gluedProjects };
};

const queueProjectToInsertInput = (
    project: QueueProject,
    scope: string,
    scopeView: string,
    region: string
): Queue_Projects_Insert_Input => ({
    sub_region: project.subRegion,
    cluster: project.cluster ?? null,
    project_number: project.projectNumber,
    bus_id: project.busId ?? null,
    nira_bus_id: project.niraBusId ?? null,
    facility_id: project.facilityId ?? null,
    poi_description: project.poiDescription,
    eris_capacity_mw: project.erisCapacityMw,
    nris_capacity_mw: project.nrisCapacityMw ?? null,
    fuel_type: project.fuelType,
    status: project.status ?? null,
    included: project.included,
    scope,
    scope_view: scopeView,
    region
});

/**
 * Builds a flat array of queue project insert inputs by duplicating the edited
 * projects once per scope view.
 */
export const buildQueueProjectInsertInputs = (
    editedQueueProjects: ReadonlyArray<QueueProject>,
    scopeViews: ReadonlyArray<string>,
    region: RegionType,
    scope: string
): ReadonlyArray<Queue_Projects_Insert_Input> => {
    return scopeViews.flatMap((scopeView) =>
        editedQueueProjects.map((project) =>
            queueProjectToInsertInput(project, scope, scopeView, region)
        )
    );
};

const compareByProjectNumber = (a: QueueProject, b: QueueProject): number =>
    a.projectNumber.localeCompare(b.projectNumber);

export const queueProjectsToExportObject = (
    queueProjects: ReadonlyArray<QueueProject>,
    queueProjectsExportColumns: ReadonlyArray<QueueProjectsExportColumn>,
    compareFn: (
        a: QueueProject,
        b: QueueProject
    ) => number = compareByProjectNumber
): ReadonlyArray<Record<string, string>> => {
    const sortedQueueProjects = [...queueProjects].sort(compareFn);
    return sortedQueueProjects.map((queueProject) => {
        return queueProjectsExportColumns.reduce((acc, column) => {
            acc[column.getHeader()] = column.renderExportValue(queueProject);
            return acc;
        }, {} as Record<string, string>);
    });
};
